import { Box, Flex, Text } from '@mantine/core';
import { Tooltip } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import cx from 'clsx';
import React, { ComponentType, ReactNode } from 'react';

import { useWorkMode } from 'shared/utils/useWorkMode';

import classes from './Placeholder.module.scss';

type PlaceholderTextProps = {
  text?: string | null;
  subText?: string | null;
};

type PlaceholderProps = {
  isContentOnly?: boolean;
  isError?: boolean;
  Icon: ComponentType;
  text?: string | null;
  subText?: string | null;
  className?: string;
  dataTestId?: string;
  height?: number;
};

function PlaceholderText(props: PlaceholderTextProps) {
  const { text, subText } = props;

  return (
    <Box ta="center">
      {text}
      {subText && (
        <Text fw="bold" ta="center">
          {subText}
        </Text>
      )}
    </Box>
  );
}

export function PlaceholderContent(props: PlaceholderProps & { children?: ReactNode }) {
  const { isContentOnly = false, isError, Icon, dataTestId, className, children, height } = props;
  const { isStudioEdit } = useWorkMode();

  return (
    <Flex
      h={height}
      justify="center"
      align="center"
      direction="column"
      className={cx(
        classes.placeholderContent,
        { [classes.contentOnly]: isContentOnly, [classes.error]: isError },
        className
      )}
    >
      <Text
        className={cx(classes.placeholderIcon, { [classes.studioEdit]: isStudioEdit })}
        c={isError ? 'pink.6' : 'accent.4'}
      >
        <Icon data-testid={dataTestId} />
      </Text>
      {children}
    </Flex>
  );
}

export function Placeholder(props: PlaceholderProps) {
  const { text, subText, isError, height } = props;
  const { ref, width: elementWidth, height: elementHeight } = useElementSize();
  const { isStudioEdit } = useWorkMode();

  const calculatedHeight = height ?? elementHeight;
  const isIconOnly = (elementWidth && elementWidth < 300) || (calculatedHeight && calculatedHeight < 150);

  return (
    <>
      {text && isIconOnly ? (
        <Tooltip label={`${text} ${subText ?? ''}`} position="bottom" withArrow>
          <Box
            className={cx(classes.placeholderContainer, { [classes.studioEdit]: isStudioEdit })}
            ref={ref}
            h={height}
          >
            <PlaceholderContent {...props} />
          </Box>
        </Tooltip>
      ) : (
        <Flex className={cx(classes.placeholderContainer, { [classes.studioEdit]: isStudioEdit })} ref={ref} h={height}>
          <PlaceholderContent {...props}>
            {text && !isIconOnly && (
              <Box className={classes.placeholderText} c={isError ? 'pink.6' : 'systemDark.7'} size="xl">
                <PlaceholderText text={text} subText={subText} />
              </Box>
            )}
          </PlaceholderContent>
        </Flex>
      )}
    </>
  );
}
